import { Button } from '@neo/primitives/button'
import { MoreVertical } from 'lucide-react'
import { useIntegrationRollout } from 'v2/shared/hooks/use-integration-rollout'
import type { RUserSession } from 'v2/types/api/auth'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { isAciExecutionTarget, requiresAciReconnect } from '../utils/integration-availability'
import { ActionsPopover } from './actions-popover'
import ShareConnectionButton from './share-connection/share-connection-button'

export interface ExternalAppRow extends RExternalOrgApp {
  [key: string]: unknown
  isGroup: boolean
  subRows?: ExternalAppRow[]
}

interface ExternalAppActionsProps {
  app: ExternalAppRow
  currentUser?: RUserSession
  aciEnabled?: boolean
}

export const ExternalAppActions = ({
  app,
  currentUser,
  aciEnabled: aciEnabledOverride,
}: ExternalAppActionsProps) => {
  const { useAciIntegrations } = useIntegrationRollout()
  const aciEnabled = aciEnabledOverride ?? useAciIntegrations
  const { isGroup, subRows } = app

  if (isGroup) {
    // Find the first non-expired connection and get its actions and UUID
    const getFirstAvailableConnection = () => {
      if (!Array.isArray(subRows)) return null
      return subRows.find((connection) => isAciExecutionTarget(connection, aciEnabled)) || null
    }

    const availableConnection = getFirstAvailableConnection()
    const directActions = availableConnection?.actions || []
    // Use the actual connection UUID, not the group id
    const orgAppUuid = availableConnection?.id || ''
    // A group is unusable either because its connections still live on the old
    // service, or because they are simply expired. Only the first case should be
    // told to reconnect "on the new integrations service".
    const needsAciReconnect =
      Array.isArray(subRows) &&
      subRows.some((connection) => requiresAciReconnect(connection, aciEnabled))
    const unavailableLabel = needsAciReconnect ? 'Reconnect required' : 'No active connection'
    const unavailableReason = needsAciReconnect
      ? 'Reconnect this integration on the new integrations service to view its actions.'
      : 'This integration has no active connection. Reconnect it to view its actions.'

    return (
      <div className="relative flex justify-end items-center gap-2">
        <ActionsPopover
          appName={app.name}
          appProvider={app.provider}
          directActions={directActions}
          orgAppUuid={orgAppUuid}
        >
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 text-secondary"
            onClick={(e) => {
              e.stopPropagation()
            }}
            data-testid="view-actions-button"
            disabled={!availableConnection}
            disabledReason={availableConnection ? undefined : unavailableReason}
          >
            {availableConnection ? 'View Actions' : unavailableLabel}
          </Button>
        </ActionsPopover>
      </div>
    )
  }

  const isCurrentUserOwner = currentUser?.aom_uuid === app?.integrated_by?.id

  if (!isCurrentUserOwner) return null

  // A reconnect-required row has no permitted actions, so its context menu is
  // empty (see getExternalAppMenuItems). Hide the trigger too — otherwise the
  // row offers a menu button that opens onto nothing.
  const needsReconnect = requiresAciReconnect(app, aciEnabled)

  return (
    <div className="relative flex justify-end items-center gap-2">
      {!needsReconnect && (
        <ShareConnectionButton
          app={app}
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 [tr:hover_&]:opacity-100"
        />
      )}
      {!needsReconnect && (
        <Button
          variant="ghost"
          tooltipContent="Open connection actions"
          tabIndex={-1}
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 [.context-menu-open_&]:opacity-100"
          data-testid="button-open-connection-actions"
          onClick={(e) => {
            e.stopPropagation()
            const rect = e.currentTarget.getBoundingClientRect()
            const customEvent = new MouseEvent('contextmenu', {
              bubbles: true,
              cancelable: true,
              clientX: rect.left + 30,
              clientY: rect.bottom,
            })
            e.currentTarget.dispatchEvent(customEvent)
          }}
        >
          <MoreVertical size={20} />
          <span className="sr-only">Open menu</span>
        </Button>
      )}
    </div>
  )
}
